import { defineStore } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'

import { useLoader } from '@/components/composables/loader.ts'
import { DataServiceFactory, EntraIDAuthService, JobService } from '@/factories'
import type {
	IDropdown,
	IJob,
	IJobContract,
	IJobDocuments,
	IJobIndex,
	IJobInvoice,
	IJobLabor,
	IJobMaterial,
	IJobOpenAR,
	IJobOther,
	IJobPayments,
	IJobProbability,
	IJobRole,
	IJobStatus,
	IJobSubContract,
	IJobTimeline,
	IJobTotal,
	IJobType,
	IStoreEntity
} from '@/interfaces'
import { Job, JobIndex } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore, useEventStore } from '@/stores'

export interface IJobStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	roles: Ref<IJobRole[]>
	timelines: Ref<IJobTimeline[]>
	statuses: Ref<IJobStatus[]>
	probabilities: Ref<IJobProbability[]>
	scopes: Ref<string[]>
	types: Ref<IJobType[]>

	rolesDropdown: Ref<IDropdown[]>
	timelineDropdown: Ref<IDropdown[]>
	statusDropdown: Ref<IDropdown[]>
	probabilityDropdown: Ref<IDropdown[]>
	jobTypesDropdown: Ref<IDropdown[]>
	resultToggle: Ref<boolean>

	addNew: () => T
	refreshJob: (id: string) => Promise<boolean>

	getRoles: () => Promise<IJobRole[]>
	getTimelines: () => Promise<IJobTimeline[]>
	getStatuses: () => Promise<IJobStatus[]>
	getProbabilities: () => Promise<IJobProbability[]>
	getJobContract: (id: string) => Promise<IJobContract[]>
	getJobInvoice: (id: string) => Promise<IJobInvoice[]>
	getJobOpenAR: (id: string) => Promise<IJobOpenAR[]>
	getJobPayments: (id: string) => Promise<IJobPayments[]>
	getJobLabor: (id: string) => Promise<IJobLabor[]>
	getJobLaborDetails: (id: string) => Promise<string>
	getJobMaterial: (id: string) => Promise<IJobMaterial[]>
	getJobMaterialDetails: (id: string) => Promise<string>
	getJobOther: (id: string) => Promise<IJobOther[]>
	getJobOtherDetails: (id: string) => Promise<string>
	getJobSubContract: (id: string) => Promise<IJobSubContract[]>
	getJobSubContractDetails: (id: string) => Promise<string>
	getJobTotal: (id: string) => Promise<IJobTotal[]>
	getJobTotalDetails: (id: string) => Promise<string>
	getJobDocuments: (id: string) => Promise<IJobDocuments[]>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const jobService = DataServiceFactory.createService('Job', authService) as JobService

export const useJobStore = defineStore<'job-store', IJobStore<IJob, IJobIndex>>('job-store', () => {
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		pageSize,
		// indexFilter,
		// indexParams,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter,

		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash
	} = useBaseStore<IJob, 'Job', IJobIndex>('Job')

	const location = ref<google.maps.LatLngLiteral | null>(null)

	const roles = ref<IJobRole[]>([])
	const timelines = ref<IJobTimeline[]>([])
	const statuses = ref<IJobStatus[]>([])
	const probabilities = ref<IJobProbability[]>([])
	const scopes = ref<string[]>([])
	const types = ref<IJobType[]>([])
	const resultToggle = ref<boolean>(false)

	const loader = useLoader()

	const eventStore = useEventStore()

	const indexFilter = ref('currentrecords')

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const timelineDropdown = computed<IDropdown[]>(() => {
		return timelines.value.map((c: IJobTimeline) => ({ name: c.timeline, code: c.id }))
	})

	const statusDropdown = computed<IDropdown[]>(() => {
		return statuses.value.map((c: IJobStatus) => ({ name: c.name, code: c.id }))
	})

	const probabilityDropdown = computed<IDropdown[]>(() => {
		return probabilities.value.map((c: IJobProbability) => ({ name: c.probability, code: c.id }))
	})

	const jobTypesDropdown = computed<IDropdown[]>(() => {
		return types.value.map((c: IJobType) => ({ name: c.name, code: c.id }))
	})

	const rolesDropdown = computed(() => {
		return roles.value.map((c: IJobRole) => ({ name: c.role, code: c.role }))
	})

	const filterList = ref([
		{ value: 'allrecords', label: 'All Records' },
		{ value: 'currentrecords', label: 'Current Records' }
	])

	const viewList = ref([{ value: 'table', icon: 'table', label: 'Table' }])

	const indexParams = computed(() => {
		if (indexFilter.value === 'allrecords') {
			return {}
		} else if (indexFilter.value === 'currentrecords') {
			const date = new Date()
			date.setDate(date.getDate() - 180)

			const year = date.getFullYear()

			const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
			const day = String(date.getDate()).padStart(2, '0')

			const formattedDate = `${year}-${month}-${day}`

			return { dueDate: formattedDate }
		} else {
			return {}
		}
	})

	async function getIndex(): Promise<JobIndex[]> {
		return jobService
			.getIndex(indexParams.value as object)
			.then((data) => {
				records.value = data
				return records.value
			})
			.catch((err) => {
				console.error(err)
				records.value = []
				return records.value
			})
	}

	async function get(id: string): Promise<Job> {
		if (id === '0') {
			return addNew()
		}
		return jobService
			.get(encodeURIComponent(id))
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as Job
				return activeRecord.value
			})
	}

	async function refreshJob(id: string): Promise<boolean> {
		try {
			const job = await get(id)
			if (job && job.eventId) {
				await eventStore.get(String(job.eventId))
				return true
			}
			return false
		} catch (err) {
			console.error(err)
			return false
		}
	}

	function addNew(): Job {
		const record = new Job({} as Job)
		// record.createBy = 969
		activeRecord.value = record
		activeRecordSavedHash.value = calcHash(record)
		return activeRecord.value
	}

	async function save(record: Job): Promise<Job> {
		if (record.id === undefined || record.id === 0) {
			return jobService
				.add(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					// activeRecord.value = {} as Job
					// return activeRecord.value
					throw err
				})
		} else {
			console.log(record)
			return jobService
				.update(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					activeRecord.value = {} as Job
					return activeRecord.value
					throw err
				})
		}
	}

	async function getRoles(): Promise<IJobRole[]> {
		return jobService
			.getRoles()
			.then((data) => {
				roles.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getTimelines(): Promise<IJobTimeline[]> {
		return jobService
			.getTimelines()
			.then((data) => {
				timelines.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getScopes(): Promise<string[]> {
		return jobService
			.getScopes()
			.then((data) => {
				scopes.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getStatuses(): Promise<IJobStatus[]> {
		return jobService
			.getStatuses()
			.then((data) => {
				statuses.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getProbabilities(): Promise<IJobProbability[]> {
		return jobService
			.getProbabilities()
			.then((data) => {
				probabilities.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getTypes(): Promise<IJobType[]> {
		return jobService
			.getTypes()
			.then((data) => {
				types.value = data
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobContract(id: string): Promise<IJobContract[]> {
		return jobService
			.getJobContract(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobInvoice(id: string): Promise<IJobInvoice[]> {
		return jobService
			.getJobInvoice(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobOpenAR(id: string): Promise<IJobOpenAR[]> {
		return jobService
			.getJobOpenAR(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobPayments(id: string): Promise<IJobPayments[]> {
		return jobService
			.getJobPayments(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobLabor(id: string): Promise<IJobLabor[]> {
		return jobService
			.getJobLabor(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobLaborDetails(id: string): Promise<string> {
		return jobService
			.getJobLaborDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getJobMaterial(id: string): Promise<IJobMaterial[]> {
		return jobService
			.getJobMaterial(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobMaterialDetails(id: string): Promise<string> {
		return jobService
			.getJobMaterialDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getJobOther(id: string): Promise<IJobOther[]> {
		return jobService
			.getJobOther(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobOtherDetails(id: string): Promise<string> {
		return jobService
			.getJobOtherDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getJobSubContract(id: string): Promise<IJobSubContract[]> {
		return jobService
			.getJobSubContract(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobSubContractDetails(id: string): Promise<string> {
		return jobService
			.getJobSubContractDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getJobTotal(id: string): Promise<IJobTotal[]> {
		return jobService
			.getJobTotal(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	async function getJobTotalDetails(id: string): Promise<string> {
		return jobService
			.getJobTotalDetails(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return ''
			})
	}

	async function getJobDocuments(id: string): Promise<IJobDocuments[]> {
		return jobService
			.getJobDocuments(encodeURIComponent(id))
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	watch(
		() => indexParams.value,
		async () => {
			loader.show()
			currentPage.value = 1
			await getIndex()
			loader.hide()
		}
	)

	if (!roles.value.length) {
		getRoles()
	}

	if (!timelines.value.length) {
		getTimelines()
	}

	if (!scopes.value.length) {
		getScopes()
	}

	if (!probabilities.value.length) {
		getProbabilities()
	}

	if (!statuses.value.length) {
		getStatuses()
	}

	if (!types.value.length) {
		getTypes()
	}

	return {
		id,
		records,
		activeRecord,
		searchTerm,
		currentPage,
		preFilters,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		location,
		roles,
		timelines,
		statuses,
		probabilities,
		scopes,
		types,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,
		getAuthStore,

		rolesDropdown,
		timelineDropdown,
		statusDropdown,
		probabilityDropdown,
		jobTypesDropdown,
		resultToggle,
		viewList,
		filterList,
		pageSize,

		clearSearch,
		clearActive,
		get,
		getIndex,
		addNew,
		save,
		getRoles,
		getTimelines,
		getStatuses,
		getProbabilities,
		getTypes,
		getScopes,
		getJobContract,
		getJobInvoice,
		getJobOpenAR,
		getJobPayments,
		getJobLabor,
		getJobLaborDetails,
		getJobMaterial,
		getJobMaterialDetails,
		getJobOther,
		getJobOtherDetails,
		getJobSubContract,
		getJobSubContractDetails,
		getJobTotal,
		getJobTotalDetails,
		refreshJob,
		getJobDocuments,

		canRead,
		canEdit,
		canAdd,
		calcHash,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
})
